import { UseQueryResult } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  EffectivePrivacy,
  PrivacyState,
  ReadableAccessMetadata,
  UserLike,
} from '@opallabs/concepts'
import { Spacing } from '@opallabs/gem'
import { pluralize } from '@opallabs/lambda'
import { AvatarList, DotLoader } from '@opallabs/lapidary'
import { PermissionsTextSummary } from '../../PermissionsTextSummary'
import { useComputedAccess } from '../../useComputedAccessData'
import * as styles from './styles'

const MAX_AVATAR_COUNT = 4

export type PermissionSummaryContentProps = {
  effectivePrivacy?: EffectivePrivacy | null
  metadataQuery: UseQueryResult<ReadableAccessMetadata | undefined>
  ownerId?: string
  resourceName: string
}

const mapCollaborators = (collaborator: UserLike) => ({
  value: collaborator.uuid,
  label: collaborator.name,
  avatarUrl: collaborator.asset?.url,
  resource_type: collaborator.type,
  isActive: collaborator.isActive,
})

const getMemberSpaceCountText = (memberCount: number, spaceCount: number) => {
  const adjustedMemberCount =
    memberCount > MAX_AVATAR_COUNT ? memberCount - MAX_AVATAR_COUNT : 0

  if (!adjustedMemberCount && !spaceCount) return ''

  if (spaceCount && !adjustedMemberCount)
    return `+${spaceCount} ${pluralize('space', spaceCount)}`

  if (adjustedMemberCount && !spaceCount)
    return `+${adjustedMemberCount} ${adjustedMemberCount > 1 ? 'people' : 'person'}`

  return `+${adjustedMemberCount} ${adjustedMemberCount > 1 ? 'people' : 'person'} and ${spaceCount} ${pluralize('space', spaceCount)}`
}

export const PermissionSummaryContent: React.FC<
  PermissionSummaryContentProps
> = ({ effectivePrivacy, metadataQuery, ownerId, resourceName }) => {
  const { data: accessMetadata, isFetching: metadataLoading } = metadataQuery
  const {
    collaborators,
    isLoading,
    memberCount,
    owner,
    spacesCount,
    workspaceResource,
  } = useComputedAccess({ accessMetadata, ownerId })

  const collaboratorsWithOwner = owner
    ? [owner, ...collaborators]
    : collaborators

  const { privacyState, showMemberList } = useMemo(() => {
    const privacyState = workspaceResource?.inheritsMembership
      ? PrivacyState.Public
      : PrivacyState.Private
    const showMemberList =
      privacyState === PrivacyState.Private &&
      effectivePrivacy !== EffectivePrivacy.EffectivelyPublic

    return { privacyState, showMemberList }
  }, [effectivePrivacy, workspaceResource])

  if (metadataLoading || isLoading) {
    return (
      <div css={styles.loaderContainer}>
        <DotLoader height={129} />
      </div>
    )
  }

  return (
    <div css={styles.container}>
      <PermissionsTextSummary
        effectivePrivacy={effectivePrivacy ?? null}
        privacyState={privacyState}
        memberCount={memberCount}
        resourceDisplayType={resourceName}
        spaceCount={spacesCount}
      />
      {showMemberList && (
        <div css={styles.membersAndSpaces}>
          <AvatarList
            avatarSize={Spacing.L}
            hideMoreItemsContainer={true}
            maxDisplayedItems={MAX_AVATAR_COUNT}
            overlapAmount={Spacing.S}
            items={collaboratorsWithOwner.map(mapCollaborators)}
          />
          <span>{getMemberSpaceCountText(memberCount, spacesCount)}</span>
        </div>
      )}
    </div>
  )
}
